import React from 'react';
import { formatDate } from '~/components/vdp/InspectionReport/helpers';
import { nativeSrpPayload } from '~/native/home/utils';

const noActiveSoftPullText =
  'Start shopping with personalized terms—no hit to your credit';
const activeSoftPullText = 'You’re approved for monthly payments up to:';
const expiredSoftPullText = (date: string | null) => {
  return (
    <>
      Your financing terms expired
      {date && (
        <>
          {' on '}
          <span className="font-bold">{formatDate(date)}</span>
        </>
      )}
      . Refresh to continue shopping with real, personalized payments.
    </>
  );
};

export enum FinanceCardStatus {
  Active = 'active',
  Expired = 'expired',
  Default = 'default'
}

enum FinanceCardTitle {
  HasPull = 'Your Financing',
  Default = 'Financing made easy'
}

enum StatusText {
  Active = 'Pre-qualified',
  Expired = 'Terms expired',
  Soon = 'Expires soon',
  Default = ''
}

export type FinanceCardData = {
  buttonTitle: string;
  buttonUrl: string;
  nativePayload?: string;
  openNativeSoftPullModal?: boolean;
  status: FinanceCardStatus;
  statusColor: string | null;
  statusText: StatusText;
  title: FinanceCardTitle;
  description: string | React.JSX.Element;
};

type OwnProps = {
  expiredDate: string | null;
  hasExpiredSoftPull: boolean;
  hasNoSoftPullStatus: boolean;
  hasSoftPullExpiringSoon: boolean;
  hasValidSoftPull: boolean;
};

export const defaultFinanceCardData: FinanceCardData = {
  buttonTitle: 'Get pre-qualified',
  buttonUrl: '/financing',
  openNativeSoftPullModal: true,
  status: FinanceCardStatus.Default,
  statusColor: null,
  statusText: StatusText.Default,
  title: FinanceCardTitle.Default,
  description: noActiveSoftPullText
};

export function getFinanceCardData({
  expiredDate,
  hasValidSoftPull,
  hasExpiredSoftPull,
  hasSoftPullExpiringSoon
}: OwnProps): FinanceCardData {
  switch (true) {
    case hasSoftPullExpiringSoon:
      return {
        buttonTitle: 'Shop with your terms',
        buttonUrl: '/cars',
        nativePayload: nativeSrpPayload,
        status: FinanceCardStatus.Active,
        statusColor: 'bg-yellow-2',
        statusText: StatusText.Soon,
        title: FinanceCardTitle.HasPull,
        description: activeSoftPullText
      };
    case hasValidSoftPull:
      return {
        buttonTitle: 'Shop with your terms',
        buttonUrl: '/cars',
        nativePayload: nativeSrpPayload,
        status: FinanceCardStatus.Active,
        statusColor: 'bg-green-2',
        statusText: StatusText.Active,
        title: FinanceCardTitle.HasPull,
        description: activeSoftPullText
      };
    case hasExpiredSoftPull:
      return {
        buttonTitle: 'Refresh my terms',
        buttonUrl: '/financing',
        openNativeSoftPullModal: true,
        status: FinanceCardStatus.Expired,
        statusColor: 'bg-red-2',
        statusText: StatusText.Expired,
        title: FinanceCardTitle.HasPull,
        description: expiredSoftPullText(expiredDate)
      };
    default:
      return defaultFinanceCardData;
  }
}
